/**
 * RS: Product Grid — behaviours for the "Shop All Pieces" section.
 *
 * Ported from the e3 component in the Lovable Seating prototype.
 *
 * WHY FILTERING IS DONE HERE AND NOT BY SHOPIFY
 * ---------------------------------------------
 * The store does not run Search & Discovery, so `collection.filters` is empty and
 * there is no Storefront filtering API to call. Shopify's own tag URLs are the
 * other option, but they can only AND, and the approved design needs OR within a
 * group (Velvet *or* Hemp) plus a price slider and live per-option counts. So the
 * filtering is done over the rendered cards.
 *
 * It is still URL-driven: ?type=sofa,chairs&fabric=recycled-velvet&price=500-3000
 * goes through pushState, so links are shareable, the Back button steps through
 * filter changes, and a reload restores the view.
 *
 * Each card arrives carrying its own facet values as data-facet-<key>="|a|b|", so
 * matching is a string test and no product lookup table is needed. The pipes
 * matter: testing "|velvet|" against "|recycled-velvet|" must not match.
 *
 * The one requirement is that everything being filtered is in the DOM. Liquid
 * hands over at most 50 products per page, so where a collection holds more the
 * remaining pages are fetched through the Section Rendering API and appended
 * before filtering begins. Seating has 36 visible products, so that never runs.
 *
 * Without JS the shopper gets the full server-rendered grid and real pagination;
 * the facet groups hide themselves, since selecting one could not do anything.
 * Sort is a separate GET form and works either way.
 */
(function () {
  'use strict';

  var STORE_DENSITY = 'rs-pg-density';
  var STORE_SIDEBAR = 'rs-pg-sidebar';

  /* --- swatch image preloading --------------------------------------------
     A colour swatch's photo used to be requested at the moment it was hovered:
     the handler wrote data-swatch-image into the <img> src and immediately
     flipped the card to data-swatch-active, so the fade to the new colour ran
     while the file was still in flight. The result was the lag -- an empty frame
     for as long as the fetch took, then the picture snapping in.

     Two changes fix it. The images are fetched BEFORE they are needed, as soon as
     the pointer touches the card, which buys the whole trip from the card edge to
     the swatch row. And the reveal waits for the bytes, so in the worst case the
     card simply holds its current photo a moment longer instead of going blank.

     Cache is keyed by URL and shared across every card and every grid on the
     page, which matters because the same colour photo is reused across a family
     of products. Image objects are kept, not just the URLs, so `complete` can be
     read back to tell a decoded image from one still loading. */
  var warmed = Object.create(null);

  function preload(url) {
    var img = warmed[url];
    if (img) return img;
    img = new Image();
    img.decoding = 'async';
    warmed[url] = img;
    // src is assigned last: for an image already in the HTTP cache the load event
    // can fire before this function returns, and the entry must be in `warmed`
    // by then or the next call starts a second fetch.
    img.src = url;
    return img;
  }

  // Warms the swatches a shopper can reach without expanding the row. The
  // overflow chips behind "+N" are left alone until that is clicked -- a product
  // can carry twenty colours and fetching all of them on a hover-through would
  // cost more than it saves. Runs once per card per mode.
  function warmCard(card, includeOverflow) {
    var flag = includeOverflow ? '__rsPgWarmAll' : '__rsPgWarm';
    if (card[flag]) return;
    card[flag] = true;

    var selector = includeOverflow
      ? '[data-swatch-image]'
      : '[data-swatch-image][data-overflow="false"]';
    var chips = card.querySelectorAll(selector);
    for (var i = 0; i < chips.length; i++) {
      var url = chips[i].getAttribute('data-swatch-image');
      if (url) preload(url);
    }
  }

  /* --- fabric families -----------------------------------------------------
     The catalogue splits a model into one product per fabric and hides all but
     one, so the grid on its own would show a card offering ten Velvet colours
     that could never be found by filtering for Hemp Blend. Liquid cannot fix
     that: it sees one page of 50 products and Seating holds 141, so a family's
     members are routinely pages apart.

     What Liquid does instead is emit every product on its page -- hidden ones
     included -- into a data-rs-pg-families payload. Those are collected here as
     the pages come in, and once they are all in, each card that speaks for a
     family gets its siblings' colours in its swatch row and its siblings'
     fabrics in its fabric facet. The link never changes: a swatch previews a
     photo and nothing more, which is what the approved prototype does.

     The store is per-root rather than module-level because two grids on one page
     are two collections with two sets of pages. */
  function collectFamilies(root, scope) {
    var store = root.__rsPgFamilies;
    if (!store) {
      store = root.__rsPgFamilies = { members: Object.create(null), swatches: Object.create(null) };
    }

    var payloads = scope.querySelectorAll('[data-rs-pg-families]');
    for (var i = 0; i < payloads.length; i++) {
      var data = null;
      try {
        data = JSON.parse(payloads[i].textContent);
      } catch (e) {
        // A malformed payload costs the merge, not the grid: the cards still
        // filter on what Liquid wrote on them.
        continue;
      }
      if (!data) continue;

      // Keyed by colour NAME and shared by every page, because the theme map it
      // comes from is name-keyed too -- see the note in the section.
      var swatches = data.swatches || {};
      for (var name in swatches) {
        if (!Object.prototype.hasOwnProperty.call(swatches, name)) continue;
        if (!store.swatches[name]) store.swatches[name] = swatches[name];
      }

      var members = data.members || [];
      for (var m = 0; m < members.length; m++) {
        var member = members[m];
        if (!member || !member.k || !member.u) continue;

        var family = store.members[member.k];
        if (!family) family = store.members[member.k] = [];
        // Keyed on the product url, so re-reading a payload cannot double a
        // member and double its colours.
        if (hasMember(family, member.u)) continue;
        family.push(member);
      }
    }

    return store;
  }

  function hasMember(family, url) {
    for (var i = 0; i < family.length; i++) {
      if (family[i].u === url) return true;
    }
    return false;
  }

  // Where a fabric sits in the Fabric filter's running order, which is also the
  // order its colours appear in on a card. Position in the pipe-joined string is
  // the rank, as with the filter options; an unlisted fabric -- Bananatex, Ethan
  // + Rob -- ranks last and keeps its colours without earning a facet value.
  function fabricRank(order, slug) {
    if (!slug || !order) return 1e9;
    var at = order.indexOf('|' + slug + '|');
    return at < 0 ? 1e9 : at;
  }

  function byFabric(family, order) {
    // Array#sort is stable, so members sharing a fabric keep the order Liquid
    // wrote them in, which is the collection's own order.
    return family.slice().sort(function (a, b) {
      return fabricRank(order, a.f) - fabricRank(order, b.f);
    });
  }

  // The family's fabrics as a facet value, in the same |slug| shape every other
  // facet on a card uses. `allow` is the Fabric filter's vocabulary when it
  // declares a fixed one: a card must not claim a value with no checkbox behind
  // it, because nothing could then clear it.
  function fabricFacet(family, allow) {
    var out = '|';
    for (var i = 0; i < family.length; i++) {
      var slug = family[i].f;
      if (!slug) continue;
      if (allow && allow.indexOf('|' + slug + '|') === -1) continue;
      if (out.indexOf('|' + slug + '|') !== -1) continue;
      out += slug + '|';
    }
    return out;
  }

  // Every family member's colours, in fabric order, deduped WITHIN a fabric
  // only. Hemp Blend and Bacana's Hemp are one cloth under two spellings, so
  // their shared Natural is one chip; a Natural in some other fabric would be
  // different material with a different photo, so it stays a second chip.
  function familyChips(family, swatches) {
    var taken = Object.create(null);
    var chips = [];

    for (var m = 0; m < family.length; m++) {
      var member = family[m];
      var colors = member.c || [];
      // A member with no mapped fabric dedupes against itself only.
      var within = member.f || member.u;

      for (var c = 0; c < colors.length; c++) {
        var color = colors[c];
        if (!color || !color.n) continue;

        var seen = within + '\u0000' + color.n;
        if (taken[seen]) continue;
        taken[seen] = true;

        chips.push({
          name: color.n,
          image: color.i || '',
          // A per-variant override travels on the colour; everything else is
          // looked up in the shared name-keyed map.
          swatch: color.s || swatches[color.n] || '',
          fabric: member.fl || ''
        });
      }
    }

    return chips;
  }

  function buildSwatch(chip, overflow, fallback) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'rs-pg-swatch';
    button.setAttribute('data-rs-pg-swatch', '');
    button.setAttribute('data-overflow', overflow ? 'true' : 'false');
    button.setAttribute('data-swatch-name', chip.name);
    if (chip.image) button.setAttribute('data-swatch-image', chip.image);
    // "Fog, Hemp Blend". The colour alone is ambiguous once one card carries
    // four fabrics, and two chips can honestly share a name.
    button.setAttribute('aria-label', chip.fabric ? chip.name + ', ' + chip.fabric : chip.name);
    if (fallback) button.style.color = fallback;
    if (chip.swatch) button.style.backgroundImage = 'url(' + chip.swatch + ')';
    return button;
  }

  function buildMore(action, modifier, text, label) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'rs-pg-card__more ' + modifier;
    button.setAttribute('data-rs-pg-action', action);
    button.textContent = text;
    if (label) button.setAttribute('aria-label', label);
    return button;
  }

  function paintSwatches(card, chips, limit, fallback) {
    var wrap = card.querySelector('[data-rs-pg-swatches]');
    if (!wrap) return;

    // One colour is not a choice, so the row stays out of the way. Liquid
    // rendered it hidden for exactly this case and the merge may not have found
    // anything to add.
    if (chips.length < 2) {
      wrap.hidden = true;
      return;
    }

    wrap.innerHTML = '';
    wrap.setAttribute('data-expanded', 'false');

    for (var i = 0; i < chips.length; i++) {
      wrap.appendChild(buildSwatch(chips[i], i >= limit, fallback));
    }

    if (chips.length > limit) {
      wrap.appendChild(buildMore('swatches-expand', 'rs-pg-card__more--show', '+' + (chips.length - limit), ''));
      wrap.appendChild(buildMore('swatches-collapse', 'rs-pg-card__more--hide', '\u2212', 'Show fewer colours'));
    }

    // The chips the preloader walked are gone, so let it walk the new ones.
    card.__rsPgWarm = false;
    card.__rsPgWarmAll = false;

    wrap.hidden = false;
  }

  // Runs before collectCards, because that is what snapshots a card's facets --
  // a fabric facet written after it would never be read.
  function mergeFamilies(state) {
    var root = state.root;
    if (!state.grid || root.getAttribute('data-merge-families') !== 'true') return;

    var store = root.__rsPgFamilies;
    if (!store) return;

    var param = root.getAttribute('data-fabric-param');
    var order = root.getAttribute('data-fabric-order') || '';
    var allow = root.getAttribute('data-fabric-allow') || '';
    var limit = Number(root.getAttribute('data-swatch-limit')) || 6;
    var fallback = root.getAttribute('data-swatch-fallback') || '';

    var cards = state.grid.querySelectorAll('[data-rs-pg-card][data-family-key]');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var family = store.members[card.getAttribute('data-family-key')];
      // A family of one is the product itself: Liquid already rendered it
      // correctly and there is nothing to add.
      if (!family || family.length < 2) continue;

      var sorted = byFabric(family, order);
      if (param) card.setAttribute('data-facet-' + param, fabricFacet(sorted, allow));
      paintSwatches(card, familyChips(sorted, store.swatches), limit, fallback);
    }
  }

  function read(key, fallback) {
    try {
      var v = window.localStorage.getItem(key);
      return v === null ? fallback : v;
    } catch (e) {
      return fallback;
    }
  }

  function write(key, value) {
    try { window.localStorage.setItem(key, value); } catch (e) { /* private mode */ }
  }

  function init(root) {
    if (!root || root.__rsPgReady) return;
    root.__rsPgReady = true;

    var state = {
      root: root,
      grid: root.querySelector('[data-rs-pg-grid]'),
      cards: [],
      groups: [],          // [{ param, options: Set<slug> }]
      selected: {},        // param -> array of slugs
      price: null,         // { param, min, max, floor, ceil }
      // False only while later pages are still in flight. Counts are computed
      // from the cards in the DOM, so until every page has landed a count of
      // zero can just mean "not here yet" and must not be acted on.
      settled: Number(root.getAttribute('data-total-pages') || 1) <= 1
    };

    restoreView(root);
    collectFamilies(root, root);
    collectGroups(state);
    mergeFamilies(state);
    collectCards(state);
    bindChrome(root, state);
    bindFacets(root, state);
    bindFilterLinks(root, state);
    bindCards(root);

    readUrl(state);
    syncInputs(state);
    apply(state, false);

    window.addEventListener('popstate', function () {
      readUrl(state);
      syncInputs(state);
      apply(state, false);
    });

    loadRemainingPages(state);
  }

  // --- remembered view state (density, sidebar) -----------------------------

  function restoreView(root) {
    var density = read(STORE_DENSITY, root.getAttribute('data-density') || '1');
    root.setAttribute('data-density', density === '2' ? '2' : '1');

    if (root.getAttribute('data-has-filters') === 'true') {
      root.setAttribute('data-sidebar', read(STORE_SIDEBAR, 'open') === 'closed' ? 'closed' : 'open');
    } else {
      root.setAttribute('data-sidebar', 'closed');
    }
  }

  function bindChrome(root, state) {
    root.addEventListener('click', function (event) {
      var hit = event.target.closest('[data-rs-pg-action]');
      if (!hit || !root.contains(hit)) return;

      var action = hit.getAttribute('data-rs-pg-action');

      if (action === 'density') {
        var next = root.getAttribute('data-density') === '2' ? '1' : '2';
        root.setAttribute('data-density', next);
        write(STORE_DENSITY, next);
        syncDensityLabel(root);
        return;
      }

      if (action === 'sidebar-hide' || action === 'sidebar-show') {
        var open = action === 'sidebar-show';
        root.setAttribute('data-sidebar', open ? 'open' : 'closed');
        write(STORE_SIDEBAR, open ? 'open' : 'closed');
        return;
      }

      if (action === 'drawer-open') { setDrawer(root, true); return; }
      if (action === 'drawer-close') { setDrawer(root, false); return; }

      if (action === 'clear') {
        keepInPlace(hit, function () {
          state.selected = {};
          if (state.price) { state.price.min = state.price.floor; state.price.max = state.price.ceil; }
          syncInputs(state);
          apply(state, true);
        });
        return;
      }

      if (action === 'swatches-expand' || action === 'swatches-collapse') {
        var wrap = hit.closest('[data-rs-pg-swatches]');
        if (wrap) wrap.setAttribute('data-expanded', action === 'swatches-expand' ? 'true' : 'false');

        // Expanding reveals the overflow chips, so now their photos are worth
        // fetching -- and the shopper is about to hover them.
        if (action === 'swatches-expand') {
          var expandedCard = hit.closest('[data-rs-pg-card]');
          if (expandedCard) warmCard(expandedCard, true);
        }
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') setDrawer(root, false);
    });

    // Sort is server-side; submitting on change saves a second click.
    var sortForms = root.querySelectorAll('[data-rs-pg-sort-form]');
    for (var i = 0; i < sortForms.length; i++) {
      (function (form) {
        form.addEventListener('change', function () {
          root.setAttribute('data-busy', 'true');
          form.submit();
        });
      })(sortForms[i]);
    }

    syncDensityLabel(root);
  }

  function syncDensityLabel(root) {
    var btn = root.querySelector('[data-rs-pg-action="density"]');
    if (!btn) return;
    var two = root.getAttribute('data-density') === '2';
    // The label describes what a click will do, which is the inverse of the state.
    btn.setAttribute('aria-label', two ? 'Switch to one product per row' : 'Switch to two products per row');
    toggleAll(btn.querySelectorAll('[data-rs-pg-icon="one"]'), two);
    toggleAll(btn.querySelectorAll('[data-rs-pg-icon="two"]'), !two);
  }

  function toggleAll(nodes, hidden) {
    for (var i = 0; i < nodes.length; i++) nodes[i].hidden = hidden;
  }

  function setDrawer(root, open) {
    var drawer = root.querySelector('[data-rs-pg-drawer]');
    if (!drawer) return;
    drawer.setAttribute('data-open', open ? 'true' : 'false');
    document.documentElement.style.overflow = open ? 'hidden' : '';
    if (open) {
      var first = drawer.querySelector('input, select, button');
      if (first) first.focus();
    }
  }

  // --- reading the rendered panel and grid ----------------------------------

  function collectGroups(state) {
    state.groups = [];
    var seen = {};

    // The panel is rendered twice (sidebar and drawer), so dedupe by param.
    var boxes = state.root.querySelectorAll('[data-rs-pg-facet]');
    for (var i = 0; i < boxes.length; i++) {
      var param = boxes[i].getAttribute('data-param');
      if (!param) continue;
      if (!seen[param]) {
        seen[param] = { param: param, options: [] };
        state.groups.push(seen[param]);
      }
      if (seen[param].options.indexOf(boxes[i].value) === -1) {
        seen[param].options.push(boxes[i].value);
      }
    }

    var price = state.root.querySelector('[data-rs-pg-price]');
    if (price) {
      var min = price.querySelector('[data-rs-pg-price-min]');
      var max = price.querySelector('[data-rs-pg-price-max]');
      if (min && max) {
        state.price = {
          param: price.getAttribute('data-param') || 'price',
          step: Number(min.step) || 50,
          floor: Number(min.min),
          ceil: Number(max.max),
          min: Number(min.min),
          max: Number(max.max)
        };
      }
    }
  }

  function collectCards(state) {
    state.cards = [];
    if (!state.grid) return;
    var nodes = state.grid.querySelectorAll('[data-rs-pg-card]');
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      var facets = {};
      for (var g = 0; g < state.groups.length; g++) {
        var param = state.groups[g].param;
        facets[param] = node.getAttribute('data-facet-' + param) || '|';
      }
      state.cards.push({
        node: node,
        facets: facets,
        price: Number(node.getAttribute('data-price') || 0)
      });
    }
  }

  // --- URL <-> state --------------------------------------------------------

  function readUrl(state) {
    var params = new URLSearchParams(window.location.search);
    state.selected = {};

    for (var i = 0; i < state.groups.length; i++) {
      var param = state.groups[i].param;
      var raw = params.get(param);
      if (!raw) continue;
      var picked = raw.split(',').filter(function (slug) {
        return slug && state.groups[i].options.indexOf(slug) !== -1;
      });
      if (picked.length) state.selected[param] = picked;
    }

    if (state.price) {
      state.price.min = state.price.floor;
      state.price.max = state.price.ceil;
      var band = params.get(state.price.param);
      if (band) {
        var parts = band.split('-');
        var lo = Number(parts[0]);
        var hi = Number(parts[1]);
        if (!isNaN(lo)) state.price.min = Math.max(state.price.floor, lo);
        if (!isNaN(hi)) state.price.max = Math.min(state.price.ceil, hi);
      }
    }
  }

  function writeUrl(state) {
    var params = new URLSearchParams(window.location.search);

    for (var i = 0; i < state.groups.length; i++) {
      var param = state.groups[i].param;
      var picked = state.selected[param];
      if (picked && picked.length) params.set(param, picked.join(','));
      else params.delete(param);
    }

    if (state.price) {
      var full = state.price.min <= state.price.floor && state.price.max >= state.price.ceil;
      if (full) params.delete(state.price.param);
      else params.set(state.price.param, state.price.min + '-' + state.price.max);
    }

    // Filtering always returns to the top of the result set.
    params.delete('page');

    var query = params.toString();
    var url = window.location.pathname + (query ? '?' + query : '');
    window.history.pushState({ rsPg: true }, '', url);
  }

  // --- facet inputs ---------------------------------------------------------

  function bindFacets(root, state) {
    root.addEventListener('change', function (event) {
      var box = event.target;
      if (!box.matches || !box.matches('[data-rs-pg-facet]')) return;

      var param = box.getAttribute('data-param');
      var slug = box.value;
      var picked = state.selected[param] ? state.selected[param].slice() : [];
      var at = picked.indexOf(slug);

      if (box.checked && at === -1) picked.push(slug);
      if (!box.checked && at !== -1) picked.splice(at, 1);

      if (picked.length) state.selected[param] = picked;
      else delete state.selected[param];

      keepInPlace(box, function () {
        syncInputs(state);
        apply(state, true);
      });
    });

    bindPrice(root, state);
  }

  // A "Browse by product type" tile is a real link to the filtered collection, so
  // it works without JS and a crawler can follow it. With JS there is no reason to
  // throw the page away and rebuild it -- the grid is already on screen, so apply
  // the filter in place and bring the shopper down to the results, which is what
  // the prototype does.
  //
  // The listener sits on the document because the tiles live in a different
  // section, above this one.
  function bindFilterLinks(root, state) {
    document.addEventListener('click', function (event) {
      var link = event.target.closest('[data-rs-pg-filter-link]');
      if (!link) return;

      var param = link.getAttribute('data-param');
      var raw = link.getAttribute('data-value');
      if (!param || !raw) return;

      var group = null;
      for (var i = 0; i < state.groups.length; i++) {
        if (state.groups[i].param === param) { group = state.groups[i]; break; }
      }
      if (!group) return;

      // A tile can name several values -- "Sofas & Loveseats" is two product
      // types, and a tile may list both the current and the tidied-up spelling of
      // one. Keep whichever actually exist; the group ORs them together.
      var wanted = raw.split(',');
      var usable = [];
      for (var w = 0; w < wanted.length; w++) {
        var slug = wanted[w].trim();
        if (slug && group.options.indexOf(slug) !== -1 && usable.indexOf(slug) === -1) {
          usable.push(slug);
        }
      }

      // Nothing on this page carries any of them. Leave the click alone: the
      // browser follows the href, which is at least honest about landing the
      // shopper on an unfiltered grid rather than appearing to do something here.
      if (!usable.length) return;

      event.preventDefault();

      // A tile is a jump to one category, not another tick on top of what is
      // already selected -- so it replaces that group rather than adding to it.
      state.selected[param] = usable;
      syncInputs(state);
      apply(state, true);

      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      root.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    });
  }

  function bindPrice(root, state) {
    if (!state.price) return;

    var wraps = root.querySelectorAll('[data-rs-pg-price]');

    for (var i = 0; i < wraps.length; i++) {
      (function (wrap) {
        var min = wrap.querySelector('[data-rs-pg-price-min]');
        var max = wrap.querySelector('[data-rs-pg-price-max]');
        if (!min || !max) return;

        var step = Number(min.step || 1);

        function drag() {
          var lo = Number(min.value);
          var hi = Number(max.value);
          // Keep a step between the thumbs so both stay separately grabbable.
          if (lo > hi - step) {
            if (document.activeElement === min) { lo = hi - step; min.value = String(lo); }
            else { hi = lo + step; max.value = String(hi); }
          }
          state.price.min = lo;
          state.price.max = hi;
          paintPrice(state);
        }

        min.addEventListener('input', drag);
        max.addEventListener('input', drag);

        // Commit on release rather than on every pixel of the drag.
        function commit() {
          keepInPlace(wrap, function () { apply(state, true); });
        }
        min.addEventListener('change', commit);
        max.addEventListener('change', commit);
      })(wraps[i]);
    }
  }

  function paintPrice(state) {
    if (!state.price) return;
    var p = state.price;
    var span = p.ceil - p.floor || 1;
    var wraps = state.root.querySelectorAll('[data-rs-pg-price]');

    for (var i = 0; i < wraps.length; i++) {
      var wrap = wraps[i];
      var fill = wrap.querySelector('[data-rs-pg-price-fill]');
      var outMin = wrap.querySelector('[data-rs-pg-price-out-min]');
      var outMax = wrap.querySelector('[data-rs-pg-price-out-max]');
      var format = wrap.getAttribute('data-money-format') || '${{amount}}';

      if (fill) {
        fill.style.left = ((p.min - p.floor) / span) * 100 + '%';
        fill.style.width = ((p.max - p.min) / span) * 100 + '%';
      }
      if (outMin) outMin.textContent = money(format, p.min);
      if (outMax) outMax.textContent = money(format, p.max) + (p.max >= p.ceil ? '+' : '');
    }
  }

  function money(format, value) {
    // Whole units: the slider steps in currency, not cents.
    return format.replace(/\{\{\s*amount[^}]*\}\}/, Number(value).toLocaleString());
  }

  // Push state back onto BOTH panel copies, so opening the drawer after filtering
  // on desktop does not show stale checkboxes.
  function syncInputs(state) {
    var boxes = state.root.querySelectorAll('[data-rs-pg-facet]');
    for (var i = 0; i < boxes.length; i++) {
      var param = boxes[i].getAttribute('data-param');
      var picked = state.selected[param] || [];
      boxes[i].checked = picked.indexOf(boxes[i].value) !== -1;
    }

    if (state.price) {
      var mins = state.root.querySelectorAll('[data-rs-pg-price-min]');
      var maxes = state.root.querySelectorAll('[data-rs-pg-price-max]');
      for (var m = 0; m < mins.length; m++) mins[m].value = String(state.price.min);
      for (var x = 0; x < maxes.length; x++) maxes[x].value = String(state.price.max);
      paintPrice(state);
    }
  }

  // Filtering hides cards, which makes the page shorter. The sidebar is sticky
  // with its own scroll, so reaching a filter near its bottom -- Collection, Fill
  // Type, Legs -- means the shopper has scrolled a long way down the page. Remove
  // most of the grid under them and the document can end up shorter than their
  // scroll offset; the browser clamps it and everything lurches.
  //
  // So: measure the control they just touched, let the grid redraw, measure again,
  // and hand back the difference. The control stays exactly where it was under the
  // cursor and the next click lands where they expect.
  function keepInPlace(anchor, fn) {
    if (!anchor || typeof anchor.getBoundingClientRect !== 'function') { fn(); return; }

    var before = anchor.getBoundingClientRect().top;
    fn();
    var drift = anchor.getBoundingClientRect().top - before;

    // Instant, not smooth -- this is a correction, not a journey.
    if (drift) window.scrollBy(0, drift);
  }

  // --- matching and applying ------------------------------------------------

  function matches(card, state, skipParam) {
    for (var param in state.selected) {
      if (!Object.prototype.hasOwnProperty.call(state.selected, param)) continue;
      if (param === skipParam) continue;

      var picked = state.selected[param];
      if (!picked.length) continue;

      // OR inside a group: any one selected value is enough.
      var facet = card.facets[param] || '|';
      var hit = false;
      for (var i = 0; i < picked.length; i++) {
        if (facet.indexOf('|' + picked[i] + '|') !== -1) { hit = true; break; }
      }
      if (!hit) return false;
    }

    if (state.price && skipParam !== state.price.param) {
      if (card.price < state.price.min || card.price > state.price.max) return false;
    }

    return true;
  }

  function apply(state, pushUrl) {
    var shown = 0;

    for (var i = 0; i < state.cards.length; i++) {
      var card = state.cards[i];
      var ok = matches(card, state, null);
      card.node.hidden = !ok;
      if (ok) shown++;
    }

    paintCounts(state);
    paintSummary(state, shown);

    if (pushUrl) writeUrl(state);
    state.root.removeAttribute('data-busy');
  }

  // A count is only useful if it answers "how many would I get if I also ticked
  // this", so each group is counted with its OWN selection ignored.
  function paintCounts(state) {
    for (var g = 0; g < state.groups.length; g++) {
      var group = state.groups[g];
      var tally = {};

      for (var i = 0; i < state.cards.length; i++) {
        var card = state.cards[i];
        if (!matches(card, state, group.param)) continue;

        var facet = card.facets[group.param] || '|';
        for (var o = 0; o < group.options.length; o++) {
          var slug = group.options[o];
          if (facet.indexOf('|' + slug + '|') !== -1) {
            tally[slug] = (tally[slug] || 0) + 1;
          }
        }
      }

      var reachable = 0;

      for (var k = 0; k < group.options.length; k++) {
        var option = group.options[k];
        var n = tally[option] || 0;
        if (n > 0) reachable++;
        var outs = state.root.querySelectorAll('[data-rs-pg-count="' + group.param + ':' + option + '"]');
        for (var c = 0; c < outs.length; c++) outs[c].textContent = '(' + n + ')';

        // An option that can no longer add anything is disabled rather than
        // removed, so the list does not reflow under the cursor.
        var boxes = state.root.querySelectorAll('[data-rs-pg-facet][data-param="' + group.param + '"][value="' + option + '"]');
        for (var b = 0; b < boxes.length; b++) {
          boxes[b].disabled = n === 0 && !boxes[b].checked;
        }
      }

      // A group with nothing behind any of its options is not a filter, it is
      // furniture. It matters for a group with a FIXED vocabulary, which Liquid
      // renders in full whether or not the collection has any of it: Fabric
      // always offers its four fabrics, and a collection of rugs has none of
      // them. Only once the pages have settled, and never while the shopper has
      // something ticked in there.
      var picked = state.selected[group.param];
      var dead = state.settled && !reachable && !(picked && picked.length);
      var panels = state.root.querySelectorAll('[data-rs-pg-group="' + group.param + '"]');
      for (var h = 0; h < panels.length; h++) panels[h].hidden = dead;
    }
  }

  function paintSummary(state, shown) {
    var root = state.root;

    var active = Object.keys(state.selected).length > 0;
    if (state.price && (state.price.min > state.price.floor || state.price.max < state.price.ceil)) {
      active = true;
    }

    toggleAll(root.querySelectorAll('[data-rs-pg-active-dot]'), !active);
    toggleAll(root.querySelectorAll('[data-rs-pg-clear]'), !active);

    var out = root.querySelector('[data-rs-pg-count-out]');
    if (out) {
      var word = shown === 1
        ? (root.getAttribute('data-count-one') || 'product')
        : (root.getAttribute('data-count-many') || 'products');
      out.textContent = shown + ' ' + word;
    }

    var empty = root.querySelector('[data-rs-pg-empty]');
    if (empty) {
      if (shown === 0) {
        empty.innerHTML = '';
        var line = document.createElement('p');
        line.textContent = root.getAttribute('data-empty-text') || 'No products match your filters.';
        empty.appendChild(line);

        if (active) {
          var link = document.createElement('a');
          link.href = root.getAttribute('data-collection-url') || window.location.pathname;
          link.textContent = root.getAttribute('data-empty-link-text') || 'Clear all filters';
          link.setAttribute('data-rs-pg-action', 'clear');
          link.addEventListener('click', function (event) { event.preventDefault(); });
          empty.appendChild(link);
        }
        empty.hidden = false;
      } else {
        empty.hidden = true;
      }
    }
  }

  // --- pulling in the rest of a collection over 50 products -----------------

  function loadRemainingPages(state) {
    var root = state.root;
    var pages = Number(root.getAttribute('data-total-pages') || 1);
    if (pages <= 1 || !state.grid) return;

    var base = root.getAttribute('data-collection-url') || window.location.pathname;
    var sectionId = root.getAttribute('data-section-id');
    if (!sectionId) return;

    root.setAttribute('data-busy', 'true');

    var jobs = [];
    for (var page = 2; page <= pages; page++) {
      jobs.push(
        fetch(base + '?page=' + page + '&section_id=' + encodeURIComponent(sectionId), {
          credentials: 'same-origin'
        })
          .then(function (response) { return response.ok ? response.text() : ''; })
          .catch(function () { return ''; })
      );
    }

    Promise.all(jobs).then(function (chunks) {
      var parser = new DOMParser();

      chunks.forEach(function (html) {
        if (!html) return;
        var doc = parser.parseFromString(html, 'text/html');

        var incoming = doc.querySelectorAll('[data-rs-pg-card]');
        for (var i = 0; i < incoming.length; i++) {
          state.grid.appendChild(document.importNode(incoming[i], true));
        }

        // The options matter as much as the cards. Each page renders its panel
        // from its OWN products, so a value that appears only on page 3 exists
        // only in page 3's panel -- merging them is what makes the filter list
        // complete. Seating needs this: Hemp, Hemp Blend and Natural are all on
        // page 3, so without it they can never be filtered on.
        mergeOptions(root, doc);

        // The siblings that make a card's swatches and its fabric facet are
        // mostly on these later pages -- the Elevate 86" Hemp product is three
        // pages from its own card -- so this is the point of fetching them at
        // all, as much as the cards themselves.
        collectFamilies(root, doc);
      });

      // Every product is in the DOM now, so server pagination is meaningless.
      var nav = root.querySelector('[data-rs-pg-pagination]');
      if (nav) nav.remove();

      state.settled = true;

      collectGroups(state);
      mergeFamilies(state);
      collectCards(state);
      rescalePrice(state);
      readUrl(state);
      syncInputs(state);
      bindCardObserver(root);
      apply(state, false);
    });
  }

  // Copy any option from a fetched page's panel that this page did not render,
  // into every panel copy (sidebar and drawer). Built from scratch rather than
  // cloned so the input ids stay unique -- the fetched markup carries the same
  // ids this page already used.
  function mergeOptions(root, doc) {
    var incoming = doc.querySelectorAll('[data-rs-pg-facet]');

    for (var i = 0; i < incoming.length; i++) {
      var box = incoming[i];
      var param = box.getAttribute('data-param');
      var slug = box.value;
      if (!param || !slug) continue;

      var already = root.querySelector(
        '[data-rs-pg-facet][data-param="' + param + '"][value="' + slug + '"]'
      );
      if (already) continue;

      var label = box.getAttribute('data-label') || slug;
      var groups = root.querySelectorAll('[data-rs-pg-group="' + param + '"]');

      for (var g = 0; g < groups.length; g++) {
        addOption(groups[g], param, slug, label, i);
      }
    }
  }

  // Where a merged option belongs in its group. Liquid writes the configured
  // running order onto the group as data-option-order="|sofa|sectional|...|", so
  // a value that first appears on page 3 can be put in its proper slot instead
  // of on the end -- otherwise the panel's order would depend on which page each
  // option happened to turn up on. Returns the <li> to insert BEFORE, or null to
  // append. Position in the pipe-joined string is itself the rank, so no split
  // is needed; an unlisted value ranks last.
  function orderRank(order, slug) {
    if (!order) return -1;
    return order.indexOf('|' + slug + '|');
  }

  function slotFor(group, list, slug) {
    var order = group.getAttribute('data-option-order');
    var rank = orderRank(order, slug);
    if (rank < 0) return null;

    var rows = list.children;
    for (var i = 0; i < rows.length; i++) {
      var box = rows[i].querySelector('[data-rs-pg-facet]');
      if (!box) continue;
      var other = orderRank(order, box.value);
      if (other < 0 || other > rank) return rows[i];
    }
    return null;
  }

  function addOption(group, param, slug, label, seed) {
    var list = group.querySelector('.rs-pg-options');
    if (!list) return;

    var id = 'rs-pg-merged-' + param + '-' + slug + '-' + seed + '-' + list.children.length;

    var input = document.createElement('input');
    input.type = 'checkbox';
    input.id = id;
    input.value = slug;
    input.setAttribute('data-rs-pg-facet', '');
    input.setAttribute('data-param', param);
    input.setAttribute('data-label', label);

    var count = document.createElement('span');
    count.className = 'rs-pg-option__count';
    count.setAttribute('data-rs-pg-count', param + ':' + slug);

    var text = document.createElement('label');
    text.setAttribute('for', id);
    text.appendChild(document.createTextNode(label + ' '));
    text.appendChild(count);

    var row = document.createElement('li');
    row.className = 'rs-pg-option';
    row.appendChild(input);
    row.appendChild(text);

    list.insertBefore(row, slotFor(group, list, slug));

    // The group was rendered empty and hidden; it has something to offer now.
    group.hidden = false;
  }

  // Price bounds were worked out from one page of products, so a cheaper or
  // dearer item on a later page would otherwise sit outside the slider's reach.
  function rescalePrice(state) {
    if (!state.price || !state.cards.length) return;

    var lo = Infinity;
    var hi = 0;
    for (var i = 0; i < state.cards.length; i++) {
      var price = state.cards[i].price;
      if (price < lo) lo = price;
      if (price > hi) hi = price;
    }
    if (lo === Infinity) return;

    var step = state.price.step || 50;
    var floor = Math.floor(lo / step) * step;
    var ceil = Math.ceil(hi / step) * step;
    if (ceil <= floor) ceil = floor + step;

    state.price.floor = floor;
    state.price.ceil = ceil;

    var mins = state.root.querySelectorAll('[data-rs-pg-price-min]');
    var maxes = state.root.querySelectorAll('[data-rs-pg-price-max]');
    for (var m = 0; m < mins.length; m++) { mins[m].min = floor; mins[m].max = ceil; }
    for (var x = 0; x < maxes.length; x++) { maxes[x].min = floor; maxes[x].max = ceil; }

    state.price.min = Math.max(state.price.min, floor);
    state.price.max = Math.min(state.price.max, ceil);
  }

  // --- card hover, swatches, image preloading ------------------------------

  function bindCards(root) {
    var grid = root.querySelector('[data-rs-pg-grid]');
    if (!grid) return;

    // Touching anywhere on a card starts fetching its swatch photos. Deliberately
    // the whole card and not the swatch row: the pointer crosses the image on its
    // way down to the chips, and that travel is the time the fetch gets to use.
    function warm(event) {
      var card = event.target.closest('[data-rs-pg-card]');
      if (card && grid.contains(card)) warmCard(card, false);
    }

    function activate(event) {
      var swatch = event.target.closest('[data-rs-pg-swatch]');
      if (!swatch || !grid.contains(swatch)) return;

      var card = swatch.closest('[data-rs-pg-card]');
      if (!card) return;

      var image = swatch.getAttribute('data-swatch-image');
      var name = swatch.getAttribute('data-swatch-name') || '';
      var target = card.querySelector('[data-rs-pg-swatch-img]');
      var label = card.querySelector('[data-rs-pg-swatch-name]');
      if (!image || !target) return;

      // The name can go up straight away -- it costs nothing and tracks the
      // pointer exactly. Only the photo has to wait.
      if (label) label.textContent = name;

      // Each hover claims a token. By the time a slow image loads the pointer may
      // have moved to another chip or off the card entirely, and that stale load
      // must not paint over whatever is current; a mismatched token drops it.
      var token = (card.__rsPgSwatchToken || 0) + 1;
      card.__rsPgSwatchToken = token;

      function reveal() {
        if (card.__rsPgSwatchToken !== token) return;
        if (target.getAttribute('src') !== image) target.setAttribute('src', image);
        card.setAttribute('data-swatch-active', 'true');
      }

      var pre = preload(image);
      if (pre.complete && pre.naturalWidth) {
        reveal();
      } else {
        // No error branch on purpose: if the photo cannot be fetched the card
        // keeps showing the image it already has, which beats fading to a blank.
        pre.addEventListener('load', reveal, { once: true });
      }
    }

    function clear(event) {
      var card = event.target.closest('[data-rs-pg-card]');
      if (!card) return;
      // Invalidate any in-flight reveal, or an image that lands after the pointer
      // has left would switch the card back on by itself.
      card.__rsPgSwatchToken = (card.__rsPgSwatchToken || 0) + 1;
      card.setAttribute('data-swatch-active', 'false');
    }

    grid.addEventListener('mouseover', warm);
    grid.addEventListener('mouseover', activate);
    grid.addEventListener('focusin', warm);
    grid.addEventListener('focusin', activate);
    grid.addEventListener('mouseleave', clear, true);
    grid.addEventListener('focusout', clear);
    grid.addEventListener('touchstart', warm, { passive: true });
    grid.addEventListener('touchstart', activate, { passive: true });

    bindCardObserver(root);
  }

  // Fetch each card's hover image only once it is near the viewport, so a long
  // grid does not pull a second image per product up front. Same intent as the
  // prototype's IntersectionObserver with a 600px rootMargin.
  function bindCardObserver(root) {
    if (!('IntersectionObserver' in window)) return;
    var grid = root.querySelector('[data-rs-pg-grid]');
    if (!grid) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        var hover = entry.target.querySelector('[data-rs-pg-hover-src]');
        if (!hover) return;
        var src = hover.getAttribute('data-rs-pg-hover-src');
        if (src && !hover.getAttribute('src')) hover.setAttribute('src', src);
      });
    }, { rootMargin: '600px' });

    var cards = grid.querySelectorAll('[data-rs-pg-card]');
    for (var i = 0; i < cards.length; i++) {
      if (cards[i].__rsPgWatched) continue;
      cards[i].__rsPgWatched = true;
      io.observe(cards[i]);
    }
  }

  function boot() {
    var roots = document.querySelectorAll('[data-rs-pg-root]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
